import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { FormattedMessage, InjectedIntl, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import ErrorMessage from '../components/errorMessage/ErrorMessage';
import { IErrorMessage } from '../components/errorMessage/errorMessage.type';
import { COLORS, MQS, SPACINGS } from '../constants/styles';
import ValidationErrors from './validationErrors';
import { ValidatorRule } from '../validator/validatorRule.constants';
import { DobFieldValidatorFactory, DobFieldValidator } from '../validator/DobValidator';
import InputField from '../components/InputField';
import store from '../../../utils/store';
import { NotificationsActions } from '../notifications/notificationsActions';

interface IDobFieldProps {
    autoFocus?: boolean;
    disabled?: boolean;
    intl: InjectedIntl;
    dayValue?: string;
    monthValue?: string;
    yearValue?: string;
    required?: boolean;
    showDobAndGenderCaution?: boolean;
    showCsUrl?: boolean;
    csUrl?: string;
    format?: string;
    validatorRules?: ValidatorRule[];
}

interface IDobFieldState {
    updated?: boolean;
    focus: boolean;
}

const DobWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    position: relative;

    @media (max-width: ${MQS.mq2Min}) {
        align-items: left;
        flex-direction: column;
    }
`;

const DobSelectWrapper = styled.div<{ disabled?: boolean }>`
    flex: ${({ disabled }) => (disabled ? '0 2 0px' : '2 2 0px')};
`;

const DobFieldsWrapper = styled.div`
    display: flex;
    & > div {
        flex-basis: 0;
        margin-right: 4px;
    }
    & > div:last-child {
        margin-right: 10px;
    }
    .year {
        flex-grow: 5;
    }
    .month {
        flex-grow: 3;
    }
    .day {
        flex-grow: 3;
    }
`;

const DobInfo = styled.p`
    color: ${COLORS.grey7};
    line-height: 1.5;
    flex: 2 3 0px;
    font-size: 0.875rem;
    padding: 0 ${SPACINGS.base * 3}px;
    margin: 0;

    a {
        color: ${COLORS.rakutenRed};
        font-weight: 500;

        &,
        &:active,
        &:hover,
        &:focus {
            text-decoration: none;
        }
    }

    @media (max-width: ${MQS.mq2Min}) {
        margin-top: 10px;
        padding: 0;
    }
`;

function getDisabledDate(
    format: string,
    dayValue?: string,
    monthValue?: string,
    yearValue?: string,
) {
    if (!dayValue || !monthValue || !yearValue) return '';
    const newFormat = format.replace(/-/g, '/');
    const day = dayValue.padStart(2, '0');
    const month = monthValue.padStart(2, '0');
    const year = yearValue;

    const dateStr = newFormat.replace('dd', day).replace('mm', month).replace('yyyy', year);

    return dateStr;
}

class DobField extends React.Component<IDobFieldProps, IDobFieldState> {
    private readonly ID: string;
    private readonly LABEL_ID: string;
    private readonly CS_ID: string;
    private readonly DD_ID: string;
    private readonly MM_ID: string;
    private readonly YYYY_ID: string;
    private dobValidator: DobFieldValidator;

    constructor(props: IDobFieldProps) {
        super(props);

        this.ID = 'dob';
        this.LABEL_ID = 'label.dob';
        this.CS_ID = 'customer_support';
        this.DD_ID = 'label.dd';
        this.MM_ID = 'label.mm';
        this.YYYY_ID = 'label.yyyy';

        this.state = { focus: false };
        this.validate = this.validate.bind(this);
        this.renderInputField = this.renderInputField.bind(this);

        const { validatorRules = [ValidatorRule.EUDobRule] } = this.props;
        const rule = validatorRules[0];
        this.dobValidator = DobFieldValidatorFactory.produceDoBFieldValidator(rule);
    }

    public render() {
        return <Field name={this.ID} validate={this.validate} render={this.renderInputField} />;
    }

    private validate(): IErrorMessage {
        const { dayValue, disabled, monthValue, yearValue, required } = this.props;

        if (disabled || (!required && !yearValue && !monthValue && !dayValue)) return '';

        let dob;
        try {
            dob = new Date(`${yearValue}-${monthValue}-${dayValue}`);
        } catch (e) {
            return ValidationErrors.fieldInvalidMessageProps(this.LABEL_ID);
        }
        if (
            isNaN(dob.getTime()) ||
            (required && (!dayValue || !monthValue || !yearValue)) ||
            dob.getDate() !== Number(dayValue) ||
            dob.getMonth() !== Number(monthValue) - 1 ||
            dob.getFullYear() !== Number(yearValue) ||
            parseInt(yearValue ?? '') < 1900
        ) {
            return TranslationHelpers.getMessageDescriptor('error.dob_invalid');
        }

        const result = this.dobValidator.validation(dob) as FormattedMessage.MessageDescriptor;
        if (!!result) {
            store.dispatch(
                NotificationsActions.showWarning(TranslationHelpers.getFormattedMessage(result.id)),
            );
        }
        return '';
    }

    private renderInputField({
        field: { onChange },
        form: { touched, errors, setFieldValue, handleBlur },
    }: FieldProps): ReactNode {
        const {
            disabled,
            intl,
            dayValue,
            monthValue,
            yearValue,
            required,
            csUrl,
            showCsUrl = true,
            showDobAndGenderCaution = false,
            format = 'dd-mm-yyyy',
        } = this.props;
        const isTouched = touched && (touched.day || touched.month || touched.year);
        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : undefined;
        const customerSupportText = intl.formatMessage(
            TranslationHelpers.getMessageDescriptor(this.CS_ID),
        );
        const ddText = intl.formatMessage(TranslationHelpers.getMessageDescriptor(this.DD_ID));
        const mmText = intl.formatMessage(TranslationHelpers.getMessageDescriptor(this.MM_ID));
        const yyyyText = intl.formatMessage(TranslationHelpers.getMessageDescriptor(this.YYYY_ID));

        const getOnBlurFunction: (fieldId: string) => Function =
            (fieldId) => (event: React.FocusEvent<HTMLInputElement>) => {
                let userInput = event.target.value;
                if (userInput.length === 1 && fieldId !== 'year') {
                    userInput = '0' + userInput;
                }
                setFieldValue(fieldId, userInput);
                this.setState({
                    focus: false,
                });
                handleBlur(event);
            };

        const processInput = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (/[^0-9]/.test(e.target.value)) return;
            onChange(e);
        };
        const onFocus = () => {
            this.setState({
                focus: true,
            });
        };
        const labelClassName =
            !!errorMessage || this.state.focus ? 'form-label-focus' : 'form-label';
        const optionalTranslated = intl.formatMessage(
            TranslationHelpers.getMessageDescriptor('label.optional'),
        );
        const requireIndicator =
            typeof required !== 'undefined' ? (required ? ' *' : ` ${optionalTranslated}`) : '';

        return (
            <DobWrapper>
                <DobSelectWrapper disabled={disabled}>
                    <label className={labelClassName} id={this.LABEL_ID}>
                        {TranslationHelpers.getFormattedMessage(this.LABEL_ID)} {requireIndicator}
                    </label>
                    {disabled ? (
                        <label style={{ margin: '0 0.6rem' }} aria-describedby={this.LABEL_ID}>
                            {getDisabledDate(format, dayValue, monthValue, yearValue)}
                        </label>
                    ) : (
                        <DobFieldsWrapper>
                            {format.split('-').map((value) => {
                                switch (value) {
                                    case 'yyyy':
                                        return (
                                            <InputField
                                                id="year"
                                                key="year"
                                                required={required}
                                                disabled={disabled}
                                                placeholder={yyyyText}
                                                value={yearValue}
                                                inputMode="numeric"
                                                maxLength={4}
                                                autoComplete={'bday-year'}
                                                onBlur={(e) => getOnBlurFunction('year')(e)}
                                                onChange={processInput}
                                                type="text"
                                                className="year"
                                                onFocus={onFocus}
                                                showRedBorder={!!errorMessage}
                                                labelMessage={TranslationHelpers.getMessageDescriptor(
                                                    'label.year',
                                                )}
                                            />
                                        );
                                    case 'mm':
                                        return (
                                            <InputField
                                                id="month"
                                                key="month"
                                                required={required}
                                                disabled={disabled}
                                                placeholder={mmText}
                                                value={monthValue}
                                                inputMode="numeric"
                                                maxLength={2}
                                                autoComplete={'bday-month'}
                                                onBlur={(e) => getOnBlurFunction('month')(e)}
                                                onChange={processInput}
                                                type="text"
                                                className="month"
                                                onFocus={onFocus}
                                                showRedBorder={!!errorMessage}
                                                labelMessage={TranslationHelpers.getMessageDescriptor(
                                                    'label.month',
                                                )}
                                            />
                                        );
                                    case 'dd':
                                        return (
                                            <InputField
                                                id="day"
                                                key="day"
                                                required={required}
                                                disabled={disabled}
                                                placeholder={ddText}
                                                value={dayValue}
                                                inputMode="numeric"
                                                maxLength={2}
                                                autoComplete={'bday-day'}
                                                onBlur={(e) => getOnBlurFunction('day')(e)}
                                                onChange={processInput}
                                                type="text"
                                                className="day"
                                                onFocus={onFocus}
                                                showRedBorder={!!errorMessage}
                                                labelMessage={TranslationHelpers.getMessageDescriptor(
                                                    'label.day',
                                                )}
                                            />
                                        );
                                    default:
                                        return null;
                                }
                            })}
                        </DobFieldsWrapper>
                    )}
                    {errorMessage && <ErrorMessage message={errorMessage} />}
                </DobSelectWrapper>
                {showDobAndGenderCaution && (
                    <DobInfo>
                        {TranslationHelpers.getFormattedMessage(
                            showCsUrl
                                ? 'dob.disabled_notification'
                                : 'dob.disabled_notification_no_csurl',
                            {
                                cs: <a href={csUrl}>{customerSupportText}</a>,
                            },
                        )}
                    </DobInfo>
                )}
            </DobWrapper>
        );
    }
}

export default injectIntl(DobField);
